import { useEffect, useState, useCallback } from "react";
import { EmailSuggestInput } from "../../components/EmailSuggestInput";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, Download, Send, CheckCircle2, XCircle, Trash2, Plus, Loader2, Eye, RefreshCw, FileCheck2, IdCard, GitBranch, Clock, Circle, FolderOpen, Bell, PenLine } from "lucide-react";
import AdminCandidateChecklist from "./AdminCandidateChecklist";
import { AccessBadge, HoldAge, LetterReviewActions, ReviewBadge, TelegramTestButton, StageBadge, STAGES, stageOf } from "../../components/ReviewActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const fmtErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : d?.msg || "Something went wrong.");
const fmtDate = (d) => (d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const EMPTY = {
  employee_name: "", employee_id: "", designation: "", department: "", joining_date: "",
  reporting_manager: "", work_location: "Kochi / Company Office", employment_type: "Permanent",
  monthly_gross_salary: "", annual_ctc: "", salary_breakup: "", perks: "",
  probation_days: 180, notice_days: 30, working_hours: "Monday to Saturday, 9:30 AM to 5:30 PM",
  weekly_off: "Sunday", special_conditions: "", letter_date: new Date().toISOString().slice(0, 10),
  personal_email: "", official_email: "", phone: "",
};

const EMP_TYPES = ["Permanent", "Temporary", "Contract", "Probationary", "Part-Time", "Outsourced", "Consultant", "Intern", "Trainee", "Casual", "Fixed-Term Contract"];

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const calcBreakup = (gross) => {
  const g = Number(gross);
  if (!g || g <= 0) return "";
  const basic = g * 0.5;
  const hra = g * 0.2;
  const allowances = g - basic - hra;
  return `Basic (50%): ${inr(basic)}\nHRA (20%): ${inr(hra)}\nOther Allowances (30%): ${inr(allowances)}\nMonthly Gross: ${inr(g)}\nNet Pay (approx.): ${inr(g)}`;
};

const Input = ({ label, required, error, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">{label}{required && <span className="text-nx-goldlight"> *</span>}</span>
    {props.type === "email"
      ? <EmailSuggestInput {...props} wrapClassName="relative w-full" className={`mt-1 w-full bg-nx-navy2 border px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none ${error ? "border-red-500/70" : "border-white/10"}`} />
      : <input {...props} className={`mt-1 w-full bg-nx-navy2 border px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none ${error ? "border-red-500/70" : "border-white/10"}`} />}
    {error && <span className="block mt-1 text-[11px] text-red-300" data-testid={`${props["data-testid"] || "field"}-error`}>{error}</span>}
  </label>
);

const FIELD_LABELS = {
  employee_name: "Employee Name", employee_id: "Employee ID", designation: "Designation", department: "Department", joining_date: "Joining Date",
  reporting_manager: "Reporting Manager", work_location: "Work Location", employment_type: "Employment Type", monthly_gross_salary: "Monthly Gross Salary",
  annual_ctc: "Annual CTC", probation_days: "Probation Period", notice_days: "Notice Period", working_hours: "Working Hours", weekly_off: "Weekly Off",
  letter_date: "Letter Date", personal_email: "Candidate Personal Email", phone: "Phone", official_email: "Official Email", salary_breakup: "Salary Breakup",
};

const humanMsg = (field, msg = "") => {
  const m = msg.toLowerCase();
  if (m.includes("at least 2")) return `${FIELD_LABELS[field] || field} must be at least 2 characters.`;
  if (m.includes("at least 1")) return `${FIELD_LABELS[field] || field} is required.`;
  if (m.includes("greater than 0")) return `${FIELD_LABELS[field] || field} must be greater than 0.`;
  if (m.includes("valid email")) return `${FIELD_LABELS[field] || field} must be a valid email address.`;
  if (m.includes("valid number") || m.includes("parsing")) return `${FIELD_LABELS[field] || field} must be a number.`;
  return `${FIELD_LABELS[field] || field}: ${msg.replace(/^Value error, /, "")}`;
};

const clientValidate = (f, terms = true) => {
  const e = {};
  if ((f.employee_name || "").trim().length < 2) e.employee_name = "Employee Name must be at least 2 characters.";
  if ((f.designation || "").trim().length < 2) e.designation = "Designation must be at least 2 characters.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.personal_email || "")) e.personal_email = "Candidate Personal Email must be a valid email address.";
  if (!terms) return e;
  if (!(f.department || "").trim()) e.department = "Department is required.";
  if (!f.joining_date) e.joining_date = "Joining Date is required.";
  if ((f.reporting_manager || "").trim().length < 2) e.reporting_manager = "Reporting Manager must be at least 2 characters.";
  if (!(Number(f.monthly_gross_salary) > 0)) e.monthly_gross_salary = "Monthly Gross Salary must be greater than 0.";
  if (!(Number(f.annual_ctc) > 0)) e.annual_ctc = "Annual CTC must be greater than 0.";
  return e;
};

const serverErrors = (detail) => {
  const out = {};
  if (Array.isArray(detail)) detail.forEach((d) => { const f = d?.loc?.[d.loc.length - 1]; if (f) out[f] = humanMsg(f, d.msg || ""); });
  return out;
};


const OfficialEmail = ({ letter, onSaved }) => {
  const [val, setVal] = useState(letter.official_email || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(letter.official_email || ""); }, [letter.official_email]);
  const dirty = val.trim().toLowerCase() !== (letter.official_email || "");
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/letters/appointment/${letter.id}/official-email`, { official_email: val.trim() });
      toast.success(val.trim() ? `Official email saved — ${letter.employee_name} now auto-fills in Chat With Us` : "Official email cleared");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save official email");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`letter-official-email-row-${letter.ref}`}>
      <span className="text-[10px] tracking-[0.18em] uppercase text-slate-500">Official email</span>
      <input type="email" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && dirty && save()} placeholder="name@nexsecure.in" className="bg-nx-navy2 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:border-nx-gold/60 outline-none w-56" data-testid={`letter-official-email-${letter.ref}`} />
      {dirty && <button type="button" onClick={save} disabled={busy} className="text-[11px] tracking-[0.1em] uppercase bg-nx-gold text-nx-navy font-semibold px-3 py-1.5 disabled:opacity-50" data-testid={`letter-official-email-save-${letter.ref}`}>{busy ? "Saving…" : "Save"}</button>}
      {!dirty && letter.official_email && <span className="text-[11px] text-emerald-400">✓ auto-fills Chat With Us</span>}
    </div>
  );
};

const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">{label}</span>
    <textarea rows={3} {...props} className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none resize-y" />
  </label>
);

const STATUS = {
  pending_ceo: { label: "Pending CEO Approval", cls: "text-amber-300 border-amber-500/50" },
  approved: { label: "Approved", cls: "text-emerald-300 border-emerald-500/50" },
  rejected: { label: "Rejected", cls: "text-red-400 border-red-500/50" },
};

const SIG_KINDS = [
  { kind: "signature", label: "CEO Signature", note: "Used on internal appointment letters" },
  { kind: "seal", label: "Company Seal", note: "Stamped on internal letters" },
  { kind: "authorised", label: "Authorised Signatory", note: "For invoices & external documents" },
];

const SignatureManager = () => {
  const [status, setStatus] = useState([]);
  const [previews, setPreviews] = useState({});
  const [busyKind, setBusyKind] = useState("");

  const loadPreview = useCallback(async (kind) => {
    try {
      const res = await api.get(`/letters/signatures/${kind}.png`, { responseType: "blob" });
      setPreviews((p) => ({ ...p, [kind]: URL.createObjectURL(res.data) }));
    } catch { setPreviews((p) => ({ ...p, [kind]: null })); }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/letters/signatures/status");
      setStatus(data.items || []);
      (data.items || []).forEach((it) => { if (it.exists) loadPreview(it.kind); });
    } catch {}
  }, [loadPreview]);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/letters/signatures/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Updated — it will appear on new documents");
      await loadPreview(kind);
      refresh();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setBusyKind(""); }
  };

  return (
    <div className="border border-nx-gold/25 bg-nx-gold/[0.04] p-5 mb-8" data-testid="signature-manager">
      <p className="text-[10px] tracking-[0.24em] uppercase text-nx-goldlight mb-1 flex items-center gap-2"><FileSignature size={13} /> Signature &amp; Seal Manager</p>
      <p className="text-xs text-slate-400 mb-4">Upload or replace signatures and the company seal — no code change needed. New documents pick them up immediately.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {SIG_KINDS.map(({ kind, label, note }) => {
          const it = status.find((s) => s.kind === kind);
          const url = previews[kind];
          return (
            <div key={kind} className="border border-white/10 bg-nx-navy2/40 p-3 flex flex-col" data-testid={`sig-card-${kind}`}>
              <p className="text-sm text-white font-medium">{label}</p>
              <p className="text-[11px] text-slate-500 mb-2">{note}</p>
              <div className="h-24 bg-white/90 flex items-center justify-center overflow-hidden mb-3 rounded-sm">
                {url ? <img src={url} alt={label} className="max-h-full max-w-full object-contain" data-testid={`sig-preview-${kind}`} />
                     : <span className="text-[11px] text-slate-500">{it?.exists ? "Loading…" : "Not set"}</span>}
              </div>
              <label className="mt-auto flex items-center justify-center gap-2 text-[11px] tracking-wide uppercase px-3 py-2 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 transition-colors cursor-pointer" data-testid={`sig-upload-${kind}`}>
                {busyKind === kind ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} className="rotate-180" />}
                {busyKind === kind ? "Uploading…" : "Upload / Replace"}
                <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={busyKind === kind}
                  onChange={(e) => { upload(kind, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};


export const AdminLetters = ({ user, onAuthFail, onCreateIdCard, prefill, onPrefillUsed }) => {
  const [letters, setLetters] = useState([]);
  const [stageFilter, setStageFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const completeTerms = (l) => {
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => { if (l[k] !== undefined && l[k] !== null && l[k] !== 0) f[k] = String(l[k]); });
    if (f.department === "To be confirmed") f.department = "";
    setForm(f);
    setEditingId(l.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [previewBusy, setPreviewBusy] = useState(false);
  const [timeline, setTimeline] = useState(null);
  const [checklistId, setChecklistId] = useState(null);
  const [docCat, setDocCat] = useState({ addable: [], fixed: [], is_ceo: false });
  const [extraDocs, setExtraDocs] = useState([]);
  const [waivedDocs, setWaivedDocs] = useState([]);
  const isCeo = user.role === "admin" || user.role === "ceo" || (user.email || "").toLowerCase() === "ceo@nexsecure.in";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/letters/appointment");
      setLetters(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error("Could not load letters");
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!prefill) return;
    setForm({ ...EMPTY, ...prefill });
    setShowForm(true);
    toast.success(`Form pre-filled from ${prefill.employee_name}'s application — complete the terms and shortlist`);
    onPrefillUsed?.();
  }, [prefill, onPrefillUsed]);

  // Keep the onboarding status dots + reminder counts current in near real time.
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    api.get("/letters/appointment/doc-catalog").then(({ data }) => setDocCat(data)).catch(() => {});
  }, []);

  const toggle = (setter, list, id) => setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const [breakupTouched, setBreakupTouched] = useState(false);
  const [ctcTouched, setCtcTouched] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setGross = (e) => {
    const v = e.target.value;
    const g = Number(v);
    setForm((f) => ({
      ...f,
      monthly_gross_salary: v,
      ...(ctcTouched ? {} : { annual_ctc: g > 0 ? String(g * 12) : "" }),
      ...(breakupTouched ? {} : { salary_breakup: calcBreakup(v) }),
    }));
  };

  const setCtc = (e) => {
    setCtcTouched(true);
    setForm((f) => ({ ...f, annual_ctc: e.target.value }));
  };

  const setBreakup = (e) => {
    setBreakupTouched(true);
    setForm((f) => ({ ...f, salary_breakup: e.target.value }));
  };

  const payload = () => ({ ...form, monthly_gross_salary: Number(form.monthly_gross_salary), annual_ctc: Number(form.annual_ctc), probation_days: Number(form.probation_days), notice_days: Number(form.notice_days), extra_docs: extraDocs, waived_docs: waivedDocs });

  const editingLetter = editingId ? letters.find((l) => l.id === editingId) : null;
  const docsApproved = (l) => !!l && (l.status === "approved" || l.stage === "letter_sent" || l.stage === "confirmed");
  const termsStage = !!editingLetter;  // Stage 2: employment terms after CEO approved the documents
  const canPreview = !!editingLetter && (docsApproved(editingLetter) || editingLetter.stage === "docs_approved");

  const preview = async () => {
    const errs = clientValidate(form);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return toast.error(`Please fix: ${Object.values(errs).join(" ")}`);
    setPreviewBusy(true);
    try {
      const res = await api.post(`/letters/appointment/preview?letter_id=${editingId || ""}`, payload(), { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else {
        let detail = err?.response?.data?.detail;
        if (err?.response?.data instanceof Blob) { try { detail = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ } }
        const se = serverErrors(detail);
        setFieldErrors(se);
        toast.error(Object.keys(se).length ? `Please fix: ${Object.values(se).join(" ")}` : fmtErr(detail));
      }
    } finally { setPreviewBusy(false); }
  };

  const closePreview = () => { URL.revokeObjectURL(previewUrl); setPreviewUrl(""); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = clientValidate(form, termsStage);
    setFieldErrors(errs);
    if (Object.keys(errs).length) return toast.error(`Please fix: ${Object.values(errs).join(" ")}`, { duration: 8000 });
    setBusy(true);
    try {
      if (editingId) {
        const { data: r } = await api.put(`/letters/appointment/${editingId}/terms`, payload());
        toast.success(r?.stage === "letter_sent" ? "Terms saved — appointment letter generated and emailed to the candidate" : r?.stage === "confirmed" ? "Terms saved — appointment confirmed" : "Appointment terms saved", { duration: 8000 });
        setEditingId(null);
      } else {
        const { data } = await api.post("/letters/appointment", payload());
        toast.success(`${data.employee_name} shortlisted (${data.ref}) — document upload link emailed`);
      }
      setForm(EMPTY);
      setFieldErrors({});
      setExtraDocs([]);
      setWaivedDocs([]);
      setBreakupTouched(false);
      setCtcTouched(false);
      setShowForm(false);
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else {
        const se = serverErrors(err?.response?.data?.detail);
        setFieldErrors(se);
        toast.error(Object.keys(se).length ? `Please fix: ${Object.values(se).join(" ")}` : fmtErr(err?.response?.data?.detail), { duration: 8000 });
      }
    } finally { setBusy(false); }
  };

  const download = async (l) => {
    setRowBusy(l.id + "pdf");
    try {
      const res = await api.get(`/letters/appointment/${l.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Appointment-Letter-${l.employee_name.replace(/\s+/g, "-")}${l.status !== "approved" ? "-DRAFT" : ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not generate PDF");
    } finally { setRowBusy(""); }
  };

  const act = async (l, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setRowBusy(l.id + action);
    try {
      if (action === "delete") await api.delete(`/letters/appointment/${l.id}`);
      else await api.post(`/letters/appointment/${l.id}/${action}`);
      toast.success(action === "send" ? `Letter emailed to ${l.personal_email}` : action === "migrate-v2" ? "Moved to the new flow — open Documents to Approve / Hold / Cancel" : `Letter ${action === "delete" ? "deleted" : action + "d"}`);
      load();
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail();
      else toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setRowBusy(""); }
  };

  const resendAcceptance = async (l) => {
    if (!window.confirm(`Send a fresh 48-hour acceptance link to ${l.personal_email}?`)) return;
    setRowBusy(l.id + "resend");
    try {
      await api.post(`/letters/appointment/${l.id}/acceptance/resend`);
      toast.success(`New acceptance link sent to ${l.personal_email}`);
      load();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setRowBusy(""); }
  };

  const replaceAcceptance = async (l) => {
    if (!window.confirm(`Ask ${l.employee_name} to resubmit a complete, properly signed acceptance letter? The original letter PDF will be emailed to ${l.personal_email} and the status reset to Pending.`)) return;
    setRowBusy(l.id + "replaceacc");
    try {
      await api.post(`/letters/appointment/${l.id}/acceptance/replace`);
      toast.success(`Resubmission request sent to ${l.personal_email}`);
      load();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setRowBusy(""); }
  };

  const sendReminder = async (l) => {
    setRowBusy(l.id + "reminder");
    try {
      const { data } = await api.post(`/letters/appointment/${l.id}/send-reminder`);
      toast.success(`Reminder sent to ${l.personal_email}${data?.pending?.length ? ` (${data.pending.length} pending)` : ""}`);
      load();
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setRowBusy(""); }
  };

  const openTimeline = async (l) => {
    setRowBusy(l.id + "timeline");
    try {
      const { data } = await api.get(`/letters/appointment/${l.id}/timeline`);
      setTimeline(data);
    } catch {
      toast.error("Could not load timeline");
    } finally { setRowBusy(""); }
  };

  const accState = (l) => {
    const a = l.acceptance;
    if (!a) return null;
    if (a.status === "uploaded") return "uploaded";
    return new Date(a.expires_at) < new Date() ? "expired" : "pending";
  };

  const downloadBlob = async (l, path, filename, key) => {
    setRowBusy(l.id + key);
    try {
      const res = await api.get(`/letters/appointment/${l.id}/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally { setRowBusy(""); }
  };

  return (
    <div data-testid="admin-letters">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-[220px] flex-1">
          <h2 className="text-lg text-white flex items-center gap-2"><FileSignature size={18} className="text-nx-goldlight" /> Staff Appointment Letters</h2>
          <p className="text-xs text-slate-500 mt-1">New flow: create = candidate shortlisted → documents uploaded → CEO approves → letter emailed automatically → signed acceptance → Confirmed.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isCeo && <TelegramTestButton api={api} />}
          <button onClick={() => setShowForm((v) => !v)} data-testid="letter-new-btn" className="flex items-center gap-2 whitespace-nowrap text-xs tracking-[0.14em] uppercase px-5 py-3 border border-nx-gold/60 text-nx-goldlight hover:bg-nx-gold/10 transition-colors">
            <Plus size={14} /> {showForm ? "Close Form" : "New Appointment"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="stage-filter">
        {[["", "All"], ...Object.entries(STAGES).map(([k, v]) => [k, v[0]])].map(([k, label]) => {
          const n = k ? letters.filter((l) => stageOf(l) === k).length : letters.length;
          return (
            <button key={k || "all"} onClick={() => setStageFilter(k)} className={`text-[10px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors ${stageFilter === k ? "border-nx-gold text-nx-goldlight bg-nx-gold/10" : "border-white/15 text-slate-400 hover:text-white"}`} data-testid={`stage-filter-${k || "all"}`}>
              {label} <span className="text-slate-500">{n}</span>
            </button>
          );
        })}
      </div>

      {isCeo && <SignatureManager />}

      {showForm && (
        <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-4 sm:p-6 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="letter-form">
          <div className="sm:col-span-2 lg:col-span-3 text-[11px] border px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-white/10 bg-white/[0.02] text-slate-300" data-testid="letter-stage-banner">
            <span className={`tracking-[0.14em] uppercase ${termsStage ? "text-fuchsia-300" : "text-nx-goldlight"}`}>{termsStage ? "Stage 2 · Employment terms" : "Stage 1 · Shortlisting"}</span>
            <span>{termsStage ? `Documents for ${editingLetter?.employee_name} are approved — enter the employment details below to generate and email the appointment letter.` : "Basic details + document checklist only. Employment terms (department, joining date, salary…) are entered after the CEO approves the uploaded documents."}</span>
          </div>
          <Input label="Employee Name" required error={fieldErrors.employee_name} value={form.employee_name} onChange={set("employee_name")} data-testid="letter-employee-name" />
          <Input label="Employee ID (blank = auto)" value={form.employee_id} onChange={set("employee_id")} placeholder="Auto-generated if empty" data-testid="letter-employee-id" />
          <Input label="Designation" required error={fieldErrors.designation} value={form.designation} onChange={set("designation")} data-testid="letter-designation" />
          {termsStage && (<>
          <Input label="Department" required error={fieldErrors.department} value={form.department} onChange={set("department")} data-testid="letter-department" />
          <Input label="Joining Date" required type="date" error={fieldErrors.joining_date} value={form.joining_date} onChange={set("joining_date")} data-testid="letter-joining-date" />
          <Input label="Reporting Manager" required error={fieldErrors.reporting_manager} value={form.reporting_manager} onChange={set("reporting_manager")} data-testid="letter-manager" />
          <Input label="Work Location" required value={form.work_location} onChange={set("work_location")} data-testid="letter-location" />
          <label className="block">
            <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400">Employment Type <span className="text-nx-goldlight">*</span></span>
            <select required value={form.employment_type} onChange={set("employment_type")} data-testid="letter-emp-type" className="mt-1 w-full bg-nx-navy2 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-nx-gold/60 outline-none">
              {EMP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <Input label="Monthly Gross Salary (₹)" required type="number" min="1" error={fieldErrors.monthly_gross_salary} value={form.monthly_gross_salary} onChange={setGross} data-testid="letter-monthly-salary" />
          <Input label="Annual CTC (₹) — auto = gross × 12" required type="number" min="1" error={fieldErrors.annual_ctc} value={form.annual_ctc} onChange={setCtc} data-testid="letter-annual-ctc" />
          <Input label="Probation Period (days)" required type="number" min="0" value={form.probation_days} onChange={set("probation_days")} data-testid="letter-probation" />
          <Input label="Notice Period (days)" required type="number" min="0" value={form.notice_days} onChange={set("notice_days")} data-testid="letter-notice" />
          <Input label="Working Hours" required value={form.working_hours} onChange={set("working_hours")} data-testid="letter-hours" />
          <Input label="Weekly Off" required value={form.weekly_off} onChange={set("weekly_off")} data-testid="letter-weekly-off" />
          <Input label="Letter Date" required type="date" value={form.letter_date} onChange={set("letter_date")} data-testid="letter-date" />
          </>)}
          <Input label="Employee Personal Email" required type="email" error={fieldErrors.personal_email} value={form.personal_email} onChange={set("personal_email")} placeholder="Given at time of joining" data-testid="letter-personal-email" />
          <Input label="Candidate Phone (for HR follow-up)" value={form.phone} onChange={set("phone")} placeholder="+91 …" data-testid="letter-phone" />
          <Input label="Official Email (@nexsecure.in, optional)" type="email" value={form.official_email} onChange={set("official_email")} placeholder="name@nexsecure.in — enables Chat With Us auto-fill" data-testid="letter-official-email" />
          {termsStage && <div className="sm:col-span-2 lg:col-span-3 grid sm:grid-cols-3 gap-4">
            <Area label="Salary Break-up (optional — auto-calculated from gross, editable)" rows={9} value={form.salary_breakup} onChange={setBreakup} data-testid="letter-breakup" />
            <Area label="Perks / Benefits (optional)" value={form.perks} onChange={set("perks")} data-testid="letter-perks" />
            <Area label="Special Conditions (optional)" value={form.special_conditions} onChange={set("special_conditions")} data-testid="letter-special" />
          </div>}
          <div className="sm:col-span-2 lg:col-span-3 border border-white/10 bg-white/[0.02] p-4" data-testid="letter-docs-section">
            <p className="text-[11px] tracking-[0.16em] uppercase text-nx-goldlight mb-1">Mandatory Documents for this Candidate</p>
            <p className="text-[11px] text-slate-500 mb-3">Fixed documents are always required{docCat.is_ceo ? " (as CEO you may uncheck them)" : " and locked for HR"}. Add more based on qualification / designation.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {docCat.fixed.map((d) => {
                const on = !waivedDocs.includes(d.id);
                return (
                  <label key={d.id} className={`flex items-center gap-2 text-[11px] px-2.5 py-1.5 border cursor-${docCat.is_ceo ? "pointer" : "not-allowed"} ${on ? "border-emerald-500/40 text-emerald-200" : "border-white/15 text-slate-500 line-through"}`} data-testid={`fixed-doc-${d.id}`}>
                    <input type="checkbox" checked={on} disabled={!docCat.is_ceo} onChange={() => toggle(setWaivedDocs, waivedDocs, d.id)} className="accent-nx-gold w-3.5 h-3.5" />
                    {d.label} <span className="text-[9px] uppercase opacity-70">fixed</span>
                  </label>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mb-2">Additional documents (optional):</p>
            <div className="flex flex-wrap gap-2">
              {docCat.addable.map((d) => (
                <label key={d.id} className={`flex items-center gap-2 text-[11px] px-2.5 py-1.5 border cursor-pointer ${extraDocs.includes(d.id) ? "border-nx-gold/50 text-nx-goldlight bg-nx-gold/[0.06]" : "border-white/15 text-slate-400 hover:text-white"}`} data-testid={`add-doc-${d.id}`}>
                  <input type="checkbox" checked={extraDocs.includes(d.id)} onChange={() => toggle(setExtraDocs, extraDocs, d.id)} className="accent-nx-gold w-3.5 h-3.5" />
                  {d.label}
                </label>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2 lg:col-span-3 flex items-center gap-3 flex-wrap">
            {canPreview && <button type="button" onClick={preview} disabled={previewBusy} data-testid="letter-preview-btn" className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3 border border-white/20 text-slate-200 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50">
              {previewBusy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Preview Letter
            </button>}
            {!canPreview && <span className="text-[11px] text-slate-500" data-testid="letter-preview-locked">Appointment letter preview unlocks after the candidate uploads all documents and the CEO approves them.</span>}
            <button type="submit" disabled={busy} data-testid="letter-submit" className="flex items-center gap-2 text-xs tracking-[0.16em] uppercase px-6 py-3 bg-nx-gold text-nx-navy font-semibold hover:brightness-110 transition-all disabled:opacity-50">
              {busy && <Loader2 size={14} className="animate-spin" />} {editingId ? (editingLetter?.stage === "docs_approved" ? "Save Terms & Generate Appointment Letter" : "Save Appointment Terms") : <>Shortlist Candidate &amp; Send Document Link</>}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {letters.length === 0 && <p className="text-sm text-slate-500 border border-white/10 p-6" data-testid="letters-empty">No appointment letters yet.</p>}
        {letters.filter((l) => !stageFilter || stageOf(l) === stageFilter).map((l) => {
          const st = STATUS[l.status] || STATUS.pending_ceo;
          const acc = accState(l);
          const v2 = l.flow === "v2";
          return (
            <div key={l.id} className="border border-white/10 bg-white/[0.02] p-4 sm:p-5" data-testid={`letter-card-${l.ref}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-white font-medium break-words">{l.employee_name}</span>
                    <span className="text-xs text-slate-500">{l.employee_id}</span>
                    {v2 ? <StageBadge letter={l} testId="letter-stage" /> : <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-1 ${st.cls}`} data-testid="letter-status">{st.label}</span>}
                    {l.terms_pending && <span className="text-[10px] tracking-[0.12em] uppercase border border-fuchsia-500/50 text-fuchsia-300 bg-fuchsia-500/10 px-2 py-1" data-testid="letter-terms-pending">Terms pending</span>}
                    {!v2 && <ReviewBadge review={l.review} testId="letter-review-badge" />}
                    <HoldAge review={l.review} testId="letter-hold-age" />
                    <AccessBadge access={l.access_suspension} testId="letter-access-badge" />
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5 break-words">
                    {l.designation} · {l.department} · Joins {fmtDate(l.joining_date)} · {money(l.monthly_gross_salary)}/mo · CTC {money(l.annual_ctc)}
                  </p>
                  {v2 && (
                    <p className="text-[11px] text-slate-400 mt-1 break-words" data-testid="letter-contact">
                      <a href={`mailto:${l.personal_email}`} className="hover:text-nx-goldlight">{l.personal_email}</a>
                      {l.phone && <> · <a href={`tel:${l.phone}`} className="text-nx-goldlight hover:underline" data-testid="letter-phone-link">{l.phone}</a></>}
                      {l.review?.reason && stageOf(l) !== "docs_submitted" && <> · <span className="text-slate-500">Reason: {l.review.reason}</span></>}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1 break-words">
                    {l.ref} · Created by {l.created_by_name || l.created_by}{l.approved_by ? ` · Approved by ${l.approved_by}` : ""}{l.sent_to?.length ? ` · Emailed to ${l.sent_to[l.sent_to.length - 1].to}` : ""}
                  </p>
                  <OfficialEmail letter={l} onSaved={load} />
                  {(() => {
                    const acc = l.acceptance || {};
                    const cd = l.candidate_docs || {};
                    const started = acc.status === "uploaded" || Object.keys(cd.files || {}).length > 0 || Object.values(cd.acks || {}).some(Boolean);
                    let stage = 1; // grey: created, not sent
                    if (acc.status === "link_sent" || l.sent_to?.length) stage = 2; // red: sent
                    if (started) stage = 3; // orange: started
                    if (cd.status === "submitted") stage = 4; // green: complete
                    const dots = [
                      { c: "bg-slate-400", t: "Created, not sent" },
                      { c: "bg-red-500", t: "Link sent" },
                      { c: "bg-orange-400", t: "Uploading — incomplete" },
                      { c: "bg-emerald-500", t: "All documents submitted" },
                    ];
                    if (l.status === "rejected" || l.status === "pending_ceo" || v2) return null;
                    return (
                      <div className="flex items-center gap-2 mt-2" data-testid="letter-status-dots" title={dots[stage - 1].t}>
                        {dots.map((d, i) => (
                          <span key={i} className={`h-2.5 w-2.5 rounded-full ${i < stage ? d.c : "bg-white/10"} ${i === stage - 1 ? "ring-2 ring-offset-1 ring-offset-nx-navy ring-white/30" : ""}`} />
                        ))}
                        <span className="text-[10px] text-slate-400 ml-1">{dots[stage - 1].t}</span>
                        {stage === 3 && (
                          <span className="text-[10px] text-orange-300 border border-orange-400/40 rounded px-1.5 py-0.5 ml-1" data-testid="letter-reminder-count">
                            {acc.reminder_count || 0} reminder{(acc.reminder_count || 0) === 1 ? "" : "s"} sent
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => openTimeline(l)} disabled={rowBusy === l.id + "timeline"} data-testid="letter-timeline" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                    {rowBusy === l.id + "timeline" ? <Loader2 size={12} className="animate-spin" /> : <GitBranch size={12} />} Timeline
                  </button>
                  <button onClick={() => setChecklistId(l.id)} data-testid="letter-documents" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors">
                    <FolderOpen size={12} /> Documents
                  </button>
                  {l.status !== "rejected" && (!v2 || docsApproved(l)) && (
                    <button onClick={() => download(l)} disabled={rowBusy === l.id + "pdf"} data-testid="letter-download" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                      {rowBusy === l.id + "pdf" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {l.status === "approved" ? "PDF" : "Draft PDF"}
                    </button>
                  )}
                  {l.terms_pending && stageOf(l) !== "cancelled" && (
                    <button onClick={() => completeTerms(l)} data-testid="letter-complete-terms" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10 transition-colors" title="Enter designation, salary and joining date">
                      <PenLine size={12} /> {l.stage === "docs_approved" ? "Enter Employment Terms & Generate Letter" : "Complete Terms"}
                    </button>
                  )}
                  {isCeo && <LetterReviewActions api={api} letter={l} onDone={load} />}
                  {isCeo && !v2 && l.status !== "rejected" && (
                    <button onClick={() => act(l, "migrate-v2", `Move ${l.employee_name} to the new document-verification flow? The CEO will then see Approve / Hold / Cancel on the documents.`)} disabled={rowBusy === l.id} data-testid="letter-migrate-v2" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 transition-colors disabled:opacity-50" title="Switch this record to the new flow">
                      <RefreshCw size={12} /> New Flow
                    </button>
                  )}
                  {isCeo && l.status === "pending_ceo" && !v2 && (
                    <>
                      <button onClick={() => act(l, "approve")} disabled={rowBusy === l.id + "approve"} data-testid="letter-approve" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                        <CheckCircle2 size={12} /> Approve
                      </button>
                      <button onClick={() => act(l, "reject", "Reject and discard this letter?")} disabled={rowBusy === l.id + "reject"} data-testid="letter-reject" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                        <XCircle size={12} /> Reject
                      </button>
                    </>
                  )}
                  {l.status === "approved" && !v2 && (
                    <button onClick={() => act(l, "send", `Email the signed letter to ${l.personal_email}?`)} disabled={rowBusy === l.id + "send"} data-testid="letter-send" className="flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase px-3 py-2 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 transition-colors disabled:opacity-50">
                      {rowBusy === l.id + "send" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Email Employee
                    </button>
                  )}
                  {(isCeo || l.status !== "approved") && (
                    <button onClick={() => act(l, "delete", "Delete this letter permanently?")} disabled={rowBusy === l.id + "delete"} data-testid="letter-delete" className="p-2 border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-500/40 transition-colors disabled:opacity-50" aria-label="Delete">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              {l.status === "approved" && (!v2 || ["letter_sent", "confirmed"].includes(l.stage)) && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center gap-2" data-testid="letter-acceptance-row">
                  {!acc && <span className="text-[11px] text-slate-500" data-testid="acceptance-not-sent">Acceptance: not sent yet — use "Email Employee" to send the letter with the 48-hour acceptance link.</span>}
                  {acc === "pending" && l.acceptance?.replace_requested_at && (
                    <span className="text-[10px] tracking-[0.12em] uppercase border border-amber-500/50 text-amber-300 px-2 py-1" data-testid="acceptance-replace-pending">
                      Signed Acceptance Pending — resubmission requested {new Date(l.acceptance.replace_requested_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  )}
                  {acc === "pending" && !l.acceptance?.replace_requested_at && (
                    <span className="text-[10px] tracking-[0.12em] uppercase border border-amber-500/50 text-amber-300 px-2 py-1" data-testid="acceptance-pending">
                      Acceptance link sent — awaiting upload (valid till {new Date(l.acceptance.expires_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })})
                    </span>
                  )}
                  {acc === "expired" && (
                    <span className="text-[10px] tracking-[0.12em] uppercase border border-red-500/50 text-red-400 px-2 py-1" data-testid="acceptance-expired">Acceptance link expired — upload not received</span>
                  )}
                  {acc === "uploaded" && (
                    <>
                      <span className="text-[10px] tracking-[0.12em] uppercase border border-emerald-500/50 text-emerald-300 px-2 py-1 flex items-center gap-1.5" data-testid="acceptance-uploaded"><FileCheck2 size={11} /> Signed acceptance uploaded</span>
                      {isCeo && (
                        <button onClick={() => replaceAcceptance(l)} disabled={rowBusy === l.id + "replaceacc"} data-testid="acceptance-replace" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                          {rowBusy === l.id + "replaceacc" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Replace Acceptance
                        </button>
                      )}
                    </>
                  )}
                  {(acc === "pending" || acc === "expired") && (
                    <>
                      <span className="text-[11px] text-red-400/80" data-testid="onboarding-blocked">Onboarding blocked until signed acceptance is uploaded</span>
                      <button onClick={() => resendAcceptance(l)} disabled={rowBusy === l.id + "resend"} data-testid="acceptance-resend" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                        {rowBusy === l.id + "resend" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Resend Link (48h)
                      </button>
                    </>
                  )}
                  {acc && (
                    <button onClick={() => downloadBlob(l, "acceptance.pdf", `Acceptance-${l.employee_name.replace(/\s+/g, "-")}.pdf`, "accpdf")} disabled={rowBusy === l.id + "accpdf"} data-testid="acceptance-pdf" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-white/15 text-slate-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-50">
                      {rowBusy === l.id + "accpdf" ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Acceptance PDF
                    </button>
                  )}
                  {acc === "uploaded" && (
                    <button onClick={() => downloadBlob(l, "acceptance-signed", `Signed-Acceptance-${l.employee_name.replace(/\s+/g, "-")}.${l.acceptance.signed_ext || "pdf"}`, "signed")} disabled={rowBusy === l.id + "signed"} data-testid="acceptance-signed-download" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50">
                      {rowBusy === l.id + "signed" ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Signed Copy
                    </button>
                  )}
                  {acc === "uploaded" && onCreateIdCard && (
                    <button onClick={() => onCreateIdCard({ name: l.employee_name, designation: l.designation, letter_id: l.id })} data-testid="acceptance-create-idcard" className="flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase px-3 py-1.5 border border-nx-gold/50 text-nx-goldlight hover:bg-nx-gold/10 transition-colors">
                      <IdCard size={11} /> Create ID Card
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {checklistId && <AdminCandidateChecklist letterId={checklistId} onClose={() => { setChecklistId(null); load(); }} isCeo={isCeo} />}

      {timeline && (
        <div data-lenis-prevent className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setTimeline(null)} data-testid="timeline-modal">
          <div className="bg-nx-navy2 border border-white/15 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-nx-navy2">
              <span className="text-sm text-white flex items-center gap-2"><GitBranch size={15} className="text-nx-goldlight" /> Candidate Journey — {timeline.employee_name}</span>
              <button onClick={() => setTimeline(null)} className="text-slate-400 hover:text-white text-xs tracking-[0.14em] uppercase px-3 py-1.5 border border-white/15" data-testid="timeline-close">Close</button>
            </div>
            <div className="p-6">
              {timeline.events.map((ev, i) => {
                const last = i === timeline.events.length - 1;
                const icon = ev.state === "done" ? <CheckCircle2 size={16} className="text-emerald-400" />
                  : ev.state === "failed" ? <XCircle size={16} className="text-red-400" />
                  : ev.state === "pending" ? <Clock size={16} className="text-amber-400" />
                  : <Circle size={16} className="text-slate-600" />;
                return (
                  <div key={i} className="flex gap-3" data-testid={`timeline-event-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="shrink-0">{icon}</div>
                      {!last && <div className={`w-px flex-1 my-1 ${ev.state === "done" ? "bg-emerald-500/40" : "bg-white/10"}`} />}
                    </div>
                    <div className={`pb-6 ${last ? "pb-0" : ""}`}>
                      <p className={`text-sm ${ev.state === "done" ? "text-white" : ev.state === "failed" ? "text-red-300" : ev.state === "pending" ? "text-amber-300" : "text-slate-500"}`}>{ev.label}</p>
                      {ev.at && <p className="text-[11px] text-slate-500 mt-0.5">{new Date(ev.at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>}
                      {ev.detail && <p className="text-[11px] text-slate-400 mt-0.5">{ev.detail}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div data-lenis-prevent className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closePreview} data-testid="letter-preview-modal">
          <div className="bg-nx-navy2 border border-white/15 w-full max-w-4xl h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="text-sm text-white flex items-center gap-2"><Eye size={15} className="text-nx-goldlight" /> {isCeo ? "Letter Preview — final (sealed & signed on create)" : "Letter Preview (draft — seal & signature added after approval)"}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => window.open(previewUrl, "_blank")} className="text-slate-400 hover:text-white text-xs tracking-[0.14em] uppercase px-3 py-1.5 border border-white/15" data-testid="letter-preview-newtab">Open in New Tab</button>
                <button onClick={closePreview} className="text-slate-400 hover:text-white text-xs tracking-[0.14em] uppercase px-3 py-1.5 border border-white/15" data-testid="letter-preview-close">Close</button>
              </div>
            </div>
            <iframe title="Letter preview" src={previewUrl} className="flex-1 w-full bg-white" data-testid="letter-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
};
